import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldHalved,
  faCircleCheck,
  faTriangleExclamation,
  faCircleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import useHttp from '../../../hooks/useHttp';

const levelConfig = {
  self: {
    color: 'success',
    icon: faCircleCheck,
    label: 'Eigenes Gerät',
  },
  nearby: {
    color: 'warning',
    icon: faTriangleExclamation,
    label: 'Anderes Gerät',
  },
  foreign: {
    color: 'error',
    icon: faCircleExclamation,
    label: 'Unbekannter Standort',
  },
};

const levelColors = {
  self: '#2e7d32',
  nearby: '#ed6c02',
  foreign: '#d32f2f',
};

function AttemptList({ attempts, currentIp, currentLocation }) {
  return (
    <>
      {/* Current session info */}
      <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
        <Typography variant='caption' color='text.secondary'>
          Dein aktueller Login:
        </Typography>
        <Typography variant='body2'>
          {currentLocation || 'Unbekannter Standort'}
          {currentIp && (
            <Typography component='span' variant='body2' color='text.secondary'>
              {' '}({currentIp})
            </Typography>
          )}
        </Typography>
      </Box>

      {/* Attempt list */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {attempts?.map((attempt, index) => {
          const config = levelConfig[attempt.level] || levelConfig.foreign;
          return (
            <Box
              key={index}
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: `${config.color}.light`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <FontAwesomeIcon
                  icon={config.icon}
                  style={{ color: levelColors[attempt.level] || levelColors.foreign, fontSize: '14px' }}
                />
                <Typography variant='body2' sx={{ fontWeight: 500, flex: 1 }}>
                  {dayjs(attempt.at).format('DD.MM.YYYY, HH:mm')} Uhr
                </Typography>
                <Chip label={config.label} color={config.color} size='small' variant='outlined' />
              </Box>
              <Typography variant='body2' color='text.secondary' sx={{ ml: 2.75 }}>
                {attempt.location || 'Unbekannter Standort'}
                {attempt.ip && ` (${attempt.ip})`}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </>
  );
}

function PasswordResetButton({ userName, getAuthHeaders }) {
  const { sendRequest, isLoading } = useHttp();
  const [sent, setSent] = useState(false);

  const handlePasswordReset = async () => {
    try {
      await sendRequest({
        url: '/api/v1/user/request-password-reset',
        method: 'POST',
        headers: getAuthHeaders({ withAuth: false }),
        body: { input: userName },
      });
      setSent(true);
      toast.success('E-Mail zum Zurücksetzen des Passworts wurde gesendet.');
    } catch {
      toast.error('Fehler beim Senden der E-Mail. Bitte versuche es über die Login-Seite.');
    }
  };

  if (sent) {
    return (
      <Button variant='contained' color='success' disabled>
        E-Mail gesendet
      </Button>
    );
  }

  return (
    <Button
      onClick={handlePasswordReset}
      variant='contained'
      color='error'
      disabled={isLoading}
      startIcon={isLoading ? <CircularProgress size={16} color='inherit' /> : null}
    >
      Passwort zurücksetzen
    </Button>
  );
}

// Banner variant for self-only attempts (dismissable alert, not blocking)
export function FailedLoginWarningBanner({ warning, onDismiss }) {
  if (!warning) return null;

  const { count } = warning;

  return (
    <Alert severity='info' onClose={onDismiss} sx={{ mb: 2 }}>
      Seit deinem letzten Login gab es{' '}
      <strong>
        {count} fehlgeschlagene{count === 1 ? 'n' : ''} Anmeldeversuch{count !== 1 ? 'e' : ''}
      </strong>{' '}
      von deiner IP-Adresse — vermutlich hast du dich vertippt. Kein Handlungsbedarf.
    </Alert>
  );
}

// Modal variant for nearby/foreign attempts (requires acknowledgement)
export function FailedLoginWarningDialog({ open, onClose, warning, userName, getAuthHeaders }) {
  if (!warning) return null;

  const { count, currentIp, currentLocation, attempts } = warning;
  const hasForeignAttempts = attempts?.some(a => a.level === 'foreign');
  const hasNearbyAttempts = attempts?.some(a => a.level === 'nearby');

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <FontAwesomeIcon
          icon={faShieldHalved}
          style={{ color: hasForeignAttempts ? '#d32f2f' : '#ed6c02' }}
        />
        <span>Sicherheitshinweis</span>
      </DialogTitle>
      <DialogContent>
        <Typography variant='body2' sx={{ mb: 2 }}>
          Seit deinem letzten Login gab es{' '}
          <strong>
            {count} fehlgeschlagene{count === 1 ? 'n' : ''} Anmeldeversuch{count !== 1 ? 'e' : ''}
          </strong>
          :
        </Typography>

        <AttemptList attempts={attempts} currentIp={currentIp} currentLocation={currentLocation} />

        {/* Contextual advice */}
        {hasForeignAttempts && (
          <Typography variant='body2' color='error.main' sx={{ mt: 2, fontWeight: 500 }}>
            Mindestens ein Versuch kam von einem unbekannten Standort. Wenn das nicht du warst,
            empfehlen wir dringend dein Passwort zu ändern.
          </Typography>
        )}
        {!hasForeignAttempts && hasNearbyAttempts && (
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
            Die Versuche kamen aus deiner Region, aber von einer anderen IP-Adresse. Falls du dich
            nicht von einem anderen Gerät angemeldet hast, empfehlen wir sicherheitshalber dein
            Passwort zu ändern.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant='text'>
          Verstanden
        </Button>
        <PasswordResetButton userName={userName} getAuthHeaders={getAuthHeaders} />
      </DialogActions>
    </Dialog>
  );
}
